<template>
  <div class="fixed inset-0 z-50 flex justify-end bg-[#1c4848]/20" @click.self="$emit('close')">
    <aside ref="panel" role="dialog" aria-modal="true" :aria-label="user.name" tabindex="-1" class="flex h-full w-full max-w-md flex-col bg-white shadow-2xl outline-none">
      <header class="flex items-center gap-3 border-b border-[#1c4848]/5 px-6 py-5">
        <UserPhoto :user="user" size="h-12 w-12 text-sm" />
        <div class="min-w-0 flex-1">
          <h2 class="truncate text-lg font-bold text-[#1c4848]">{{ user.name }}</h2>
          <p class="truncate text-xs text-[#1c4848]/55">{{ user.email ?? user.device_model ?? 'Phone with the app' }}</p>
        </div>
        <button type="button" class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[#1c4848]/50 hover:bg-[#1c4848]/5 hover:text-[#1c4848]" aria-label="Close" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.500" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </header>

      <div class="flex-1 space-y-6 overflow-y-auto px-6 py-5">
        <form v-if="isAccount" class="space-y-3" @submit.prevent="save">
          <div class="grid grid-cols-2 gap-3">
            <label class="block"><span class="mb-1 block text-xs font-bold text-[#1c4848]/60">First name</span><input v-model="form.first_name" type="text" autocomplete="off" required class="w-full rounded-xl border border-[#1c4848]/15 px-3 py-2.5 text-sm text-[#1c4848] focus:border-[#1c4848]/50 focus:outline-none"></label>
            <label class="block"><span class="mb-1 block text-xs font-bold text-[#1c4848]/60">Last name</span><input v-model="form.last_name" type="text" autocomplete="off" class="w-full rounded-xl border border-[#1c4848]/15 px-3 py-2.5 text-sm text-[#1c4848] focus:border-[#1c4848]/50 focus:outline-none"></label>
          </div>
          <label class="block"><span class="mb-1 block text-xs font-bold text-[#1c4848]/60">E-mail</span><input v-model="form.email" type="email" autocomplete="off" required class="w-full rounded-xl border border-[#1c4848]/15 px-3 py-2.5 text-sm text-[#1c4848] focus:border-[#1c4848]/50 focus:outline-none"></label>
          <p v-if="emailChanged" class="text-xs leading-relaxed text-[#8a5a00]">They will sign in with the new e-mail from now on.</p>
          <p v-if="saveError" role="alert" class="text-sm font-medium text-red-700">{{ saveError }}</p>
          <button type="submit" class="min-h-11 rounded-xl bg-[#1c4848] px-5 text-sm font-bold text-white hover:bg-[#153838] disabled:opacity-40" :disabled="!changed || saving">{{ saving ? 'Saving…' : (saved && !changed ? 'Saved' : 'Save changes') }}</button>
        </form>

        <section v-if="isAccount">
          <h3 class="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#1c4848]/50">Roles</h3>
          <div class="flex flex-wrap gap-1.5">
            <button v-for="role in visibleRoles" :key="role.id" type="button" class="min-h-9 rounded-full border px-3 text-xs font-bold transition-colors" :class="user.roles.includes(role.id) ? 'border-[#1c4848] bg-[#1c4848] text-white' : 'border-[#1c4848]/15 bg-white text-[#1c4848]/55 hover:border-[#1c4848]/40'" :aria-pressed="user.roles.includes(role.id)" @click="access.toggleRole(user, role)">{{ role.label }}</button>
          </div>
          <p class="mt-2 text-xs text-[#1c4848]/50">A click gives or takes the role right away. Any of these opens the panel for them.</p>
          <p v-if="access.error" role="alert" class="mt-2 text-sm font-medium text-red-700">{{ access.error }}</p>
        </section>

        <section>
          <h3 class="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#1c4848]/50">Details</h3>
          <dl class="divide-y divide-[#1c4848]/5 rounded-2xl border border-[#1c4848]/10">
            <div v-for="fact in facts" :key="fact.label" class="flex items-baseline justify-between gap-4 px-4 py-2.5 text-sm">
              <dt class="shrink-0 text-[#1c4848]/55">{{ fact.label }}</dt>
              <dd class="min-w-0 text-end font-medium text-[#1c4848]">{{ fact.value }}</dd>
            </div>
          </dl>
          <p v-if="!isAccount" class="mt-3 text-xs leading-relaxed text-[#1c4848]/50">This account was made by the app for one phone. It has no name, e-mail or password, so there is nothing to edit.</p>
        </section>

        <section v-if="isAccount">
          <h3 class="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#1c4848]/50">Password</h3>
          <button v-if="resetState === 'idle'" type="button" class="min-h-11 rounded-xl border border-[#1c4848]/15 px-4 text-sm font-bold text-[#1c4848] hover:bg-[#1c4848]/5" @click="resetState = 'confirm'">Send a reset link</button>
          <div v-else-if="resetState === 'confirm' || resetState === 'sending'" class="rounded-2xl bg-[#fff7e0] p-4">
            <p class="text-sm leading-relaxed text-[#8a5a00]">An e-mail goes to <strong class="break-all">{{ user.email }}</strong> now.</p>
            <div class="mt-3 flex gap-2">
              <button type="button" class="min-h-11 rounded-xl bg-[#1c4848] px-4 text-sm font-bold text-white disabled:opacity-50" :disabled="resetState === 'sending'" @click="sendReset">{{ resetState === 'sending' ? 'Sending…' : 'Send it' }}</button>
              <button type="button" class="min-h-11 rounded-xl px-4 text-sm font-bold text-[#1c4848]/60 hover:bg-white/60" @click="resetState = 'idle'">Cancel</button>
            </div>
          </div>
          <p v-else-if="resetState === 'sent'" role="status" class="text-sm font-medium text-[#1c4848]">The link is on its way to {{ user.email }}.</p>
          <p v-else role="alert" class="text-sm font-medium text-red-700">The link could not be sent. Close this and try again.</p>
        </section>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import UserPhoto from '@/components/app/dashboard/users/UserPhoto.vue';
import { useAccessStore } from '@/store/access/index.js';
import { useUsersStore } from '@/store/users/index.js';
import { regionName } from '@/utils/regionName.js';

const ENVIRONMENTS = { production: 'App Store', testflight: 'TestFlight', debug: 'Xcode build', simulator: 'Simulator' };

const props = defineProps({ user: { type: Object, required: true } });
const emit = defineEmits(['close']);

const store = useUsersStore();
const access = useAccessStore();
const panel = ref(null);
const form = reactive({ first_name: '', last_name: '', email: '' });
const saving = ref(false);
const saved = ref(false);
const saveError = ref('');
const resetState = ref('idle');

const isAccount = computed(() => props.user.kind === 'accounts');
const visibleRoles = computed(() => access.roles.filter((role) => role.name !== 'app-user'));
const emailChanged = computed(() => form.email.trim() !== (props.user.email ?? ''));
const changed = computed(() => form.first_name.trim() !== (props.user.first_name ?? '') || form.last_name.trim() !== (props.user.last_name ?? '') || emailChanged.value);

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const when = (value) => (value ? dateFormat.format(new Date(value)) : 'Unknown');

const facts = computed(() => {
  const u = props.user;
  const app = u.app_version ? (u.build ? `v${u.app_version} (${u.build})` : `v${u.app_version}`) : null;
  return [
    { label: 'Joined', value: when(u.joined_at) },
    { label: 'Premium', value: u.premium ? 'Yes' : 'No' },
    isAccount.value && { label: 'Academy maps', value: u.maps },
    u.language && { label: 'Language', value: u.language },
    u.region && { label: 'Country', value: regionName(u.region) },
    u.environment && { label: 'Installed from', value: ENVIRONMENTS[u.environment] ?? u.environment },
    u.device_model && { label: 'Phone', value: u.device_model },
    u.os_version && { label: 'iOS', value: u.os_version },
    app && { label: 'App', value: app },
    u.seen_at && { label: 'Last seen in the app', value: when(u.seen_at) },
    { label: 'Account number', value: u.id },
  ].filter(Boolean);
});

watch(() => props.user, (user) => {
  form.first_name = user.first_name ?? '';
  form.last_name = user.last_name ?? '';
  form.email = user.email ?? '';
}, { immediate: true });

function onKey(event) {
  if (event.key === 'Escape') emit('close');
}
onMounted(() => {
  panel.value?.focus();
  window.addEventListener('keydown', onKey);
  if (!access.roles.length) access.fetchOverview().catch(() => {});
});
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));

async function save() {
  saving.value = true;
  saveError.value = '';
  try {
    await store.update(props.user, { first_name: form.first_name.trim(), last_name: form.last_name.trim(), email: form.email.trim() });
    saved.value = true;
  } catch (e) {
    const errors = e?.response?.data?.errors ?? {};
    saveError.value = Object.values(errors)[0]?.[0] ?? e?.response?.data?.message ?? 'The changes could not be saved.';
  } finally {
    saving.value = false;
  }
}

async function sendReset() {
  resetState.value = 'sending';
  try {
    await store.sendPasswordReset(props.user);
    resetState.value = 'sent';
  } catch {
    resetState.value = 'failed';
  }
}
</script>
